import { useMemo, useState } from "react";
import type { FieldDefinition } from "@estelle/domain";
import { Columns3, Eye, EyeOff, GripVertical, RotateCcw } from "lucide-react";

import {
  defaultColumns,
  moveColumn,
  moveColumnTo,
  toggleColumn,
  type ColumnState,
} from "@/components/columns/columns";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Which columns a table shows, and in what order.
 *
 * Unlike the filter builder, there is no draft/Apply step: every action here
 * is already valid the instant it happens, where a half-built filter
 * condition can be invalid mid-edit. Applying immediately also means the
 * popover can stay open across several changes and the table updates as you
 * go, which is the point of a reorder control — you're watching the effect,
 * not composing a query.
 *
 * Sort used to live here too. It moved to `SortControl` (2026-09-15) because
 * grid and tile layouts have no columns, and sort hidden inside Columns was
 * missing exactly where there were no headers to click instead.
 *
 * `compact` drops the label for an icon-only trigger — the filter bar (#349)
 * sits this beside Sort and Save with no room for three words each, the same
 * reason the layout switcher next to it has never had one.
 */
export function ColumnChooser({
  value,
  fields,
  compact = false,
  onChange,
}: {
  value: ColumnState;
  fields: FieldDefinition[];
  compact?: boolean;
  onChange: (next: ColumnState) => void;
}) {
  // Only what has a column to show: a filter-only field (`displayable:
  // false`) is the filter builder's, not the chooser's.
  const offered = useMemo(
    () => fields.filter((field) => field.displayable !== false),
    [fields],
  );
  const byName = useMemo(
    () => new Map(offered.map((field) => [field.name, field])),
    [offered],
  );

  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  // Visible fields keep the caller's order (so reorder has something to act
  // on); hidden fields fall back to declaration order since a hidden field
  // has no position of its own to preserve.
  const visible: FieldDefinition[] = [];
  for (const name of value) {
    const field = byName.get(name);
    if (field) visible.push(field);
  }
  const hidden = offered.filter((field) => !value.includes(field.name));

  function drop(targetName: string) {
    if (dragging === null) return;
    const index = value.indexOf(targetName);
    if (index !== -1) onChange(moveColumnTo(value, dragging, index));
    setDragging(null);
    setOver(null);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {compact ? (
          <Button size="icon-sm" variant="outline" aria-label="Columns">
            <Columns3 />
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Columns3 />
            Columns
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-3">
        <div className="flex flex-col gap-0.5">
          {visible.map((field, index) => (
            <div
              key={field.name}
              draggable
              onDragStart={() => {
                setDragging(field.name);
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onDragOver={(event) => {
                // Without this the drop never fires: the default handling of
                // a dragover is to refuse the drop.
                event.preventDefault();
                setOver(field.name);
              }}
              onDrop={() => {
                drop(field.name);
              }}
              className={cn(
                "flex items-center gap-1 rounded-md px-1 py-0.5",
                dragging === field.name && "opacity-40",
                over === field.name &&
                  dragging !== null &&
                  dragging !== field.name &&
                  "bg-accent",
              )}
            >
              {/*
                The handle is a button so the reorder has a keyboard path.
                Drag is unreachable without a pointer, and column order is not
                a decoration — a list someone cannot arrange is a list they
                cannot read.
              */}
              <button
                type="button"
                aria-label={`Reorder ${field.label}. Use arrow keys to move.`}
                onKeyDown={(event) => {
                  if (event.key === "ArrowUp") {
                    event.preventDefault();
                    onChange(moveColumn(value, field.name, "up"));
                  }
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    onChange(moveColumn(value, field.name, "down"));
                  }
                }}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring cursor-grab rounded p-0.5 focus-visible:ring-2 focus-visible:outline-none"
              >
                <GripVertical className="size-4" aria-hidden />
              </button>

              <span className="flex-1 truncate text-sm">{field.label}</span>

              <Button
                size="icon-xs"
                variant="ghost"
                // Hiding the last visible column is refused by `toggleColumn`
                // anyway; disabled rather than a silent no-op, because a
                // control that looks live and does nothing reads as broken.
                disabled={visible.length <= 1}
                onClick={() => {
                  onChange(toggleColumn(value, field.name));
                }}
                aria-label={`Hide ${field.label}`}
              >
                <Eye />
              </Button>

              <span className="sr-only">{`Position ${String(index + 1)} of ${String(visible.length)}`}</span>
            </div>
          ))}

          {hidden.length > 0 ? (
            <div className="mt-2 flex flex-col gap-0.5 border-t pt-2">
              {hidden.map((field) => (
                <div
                  key={field.name}
                  className="flex items-center gap-1 px-1 py-0.5"
                >
                  {/* No handle: a hidden column has no position to move. */}
                  <span className="size-5 shrink-0" aria-hidden />
                  <span className="text-muted-foreground flex-1 truncate text-sm">
                    {field.label}
                  </span>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    onClick={() => {
                      onChange(toggleColumn(value, field.name));
                    }}
                    aria-label={`Show ${field.label}`}
                  >
                    <EyeOff className="text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-3 flex items-center border-t pt-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange(defaultColumns(fields));
            }}
          >
            <RotateCcw />
            Reset columns
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
